import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { env } from '@/config/env'

import { isTrackedInInventory, retryProductInventory } from '../api'
import { inventoryRefsOf } from '../product-inventory'
import type { ProductDetail, ProductInventoryRowStatus } from '../types'
import { productsKey } from './use-products'

/** How often inventory is asked again after a retry, and for how long. */
const POLL_MS = 2_000
const AWAIT_MS = 20_000

/**
 * Whether each stock item of a product made it to inventory, and the Retry for
 * the ones that did not.
 *
 * Under `products` so every product write — the save above all, which is what
 * publishes a new row — invalidates it with the rest. Idle where inventory has
 * no host: every request would be refused up front.
 *
 * A retry is only sent: inventory reads it off a stream a moment later. So for
 * a short while after one, the status is asked again until every row is in,
 * and `isAwaiting` says that is happening rather than leaving the old answer up
 * as if it were the new one.
 */
export function useProductInventory(product: ProductDetail | null) {
  const queryClient = useQueryClient()
  const refs = useMemo(() => (product ? inventoryRefsOf(product) : []), [product])
  const queryKey = [...productsKey, 'inventory', product?.id ?? '', refs.map((ref) => ref.id)]
  const isEnabled = Boolean(env.inventoryApiBaseUrl) && refs.length > 0

  const [retriedAt, setRetriedAt] = useState<number | null>(null)
  const isAwaiting = retriedAt !== null

  useEffect(() => {
    if (retriedAt === null) return
    const timer = setTimeout(() => setRetriedAt(null), AWAIT_MS)
    return () => clearTimeout(timer)
  }, [retriedAt])

  const { data, isPending, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      Promise.all(
        refs.map(
          async (ref): Promise<ProductInventoryRowStatus> => ({
            ref,
            isTracked: await isTrackedInInventory(ref),
          }),
        ),
      ),
    enabled: isEnabled,
    refetchInterval: (query) =>
      isAwaiting && query.state.data?.some((row) => !row.isTracked) ? POLL_MS : false,
  })

  const retry = useMutation({
    mutationFn: () => retryProductInventory(product!.id),
    onSuccess: () => {
      setRetriedAt(Date.now())
      return queryClient.invalidateQueries({ queryKey })
    },
  })

  const rows = data ?? []
  const missing = rows.filter((row) => !row.isTracked)

  return {
    rows,
    missing,
    isChecking: isEnabled && isPending,
    isAwaiting: isAwaiting && missing.length > 0,
    error,
    check: () => void refetch(),
    retry,
  }
}
